// src/pages/products/CreateProductPage/components/PricingTab.jsx
import React, { useState } from 'react';
import { DollarSign, Hash, Weight, Building2, Warehouse, BarChart3, Loader2, Truck, Tag } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import OffersModal from '../../components/OffersModal';
import styles from '../styles/PricingTab.module.css';

const PricingTab = ({
  formData,
  shops,
  warehouses,
  errors,
  skuValidation,
  handleInputChange,
  handleShopDataChange,
  handleOpeningStockChange
}) => {
  const { t } = useLocalization();
  const [offersModalIndex, setOffersModalIndex] = useState(null);

  // For variable products, only show basic pricing fields
  if (formData.productType === 'variable') {
    return (
      <div className={styles.formCard}>
        <div className={styles.cardHeader}>
          <h2>{t('products.basicPricingInfo')}</h2>
          <p>{t('products.basicPricingInfoDesc')}</p>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="sku">{t('products.sku')}</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  id="sku"
                  name="sku"
                  value={formData.sku}
                  onChange={handleInputChange}
                  placeholder={t('products.skuPlaceholder')}
                  className={errors.sku ? 'error' : ''}
                />
                {skuValidation?.mainSku?.isValidating && (
                  <Loader2 size={16} className={`${styles.inputIcon} ${styles.validationSpinner}`} />
                )}
              </div>
              {errors.sku && <span className={styles.errorText}>{errors.sku}</span>}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="barcode">{t('products.barcode')}</label>
              <div className={styles.inputWithIcon}>
                <Hash size={18} className={styles.inputIcon} />
                <input
                  type="text"
                  id="barcode"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleInputChange}
                  placeholder={t('products.barcodePlaceholder')}
                />
              </div>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="buyingPrice">{t('products.buyingPrice')}</label>
              <div className={styles.inputWithIcon}>
                <DollarSign size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="buyingPrice"
                  name="buyingPrice"
                  value={formData.buyingPrice}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="weight">{t('products.weight')} (kg)</label>
              <div className={styles.inputWithIcon}>
                <Weight size={18} className={styles.inputIcon} />
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For simple products, show full pricing and inventory
  return (
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.pricingInventory')}</h2>
        <p>{t('products.pricingInventoryDesc')}</p>
      </div>
      <div className={styles.cardContent}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="sku">{t('products.sku')}</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                placeholder={t('products.skuPlaceholder')}
                className={errors.sku ? 'error' : ''}
              />
              {skuValidation?.mainSku?.isValidating && (
                <Loader2 size={16} className={`${styles.inputIcon} ${styles.validationSpinner}`} />
              )}
            </div>
            {errors.sku && <span className={styles.errorText}>{errors.sku}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="barcode">{t('products.barcode')}</label>
            <div className={styles.inputWithIcon}>
              <Hash size={18} className={styles.inputIcon} />
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                placeholder={t('products.barcodePlaceholder')}
              />
            </div>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="buyingPrice">{t('products.buyingPrice')}</label>
            <div className={styles.inputWithIcon}>
              <DollarSign size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="buyingPrice"
                name="buyingPrice"
                value={formData.buyingPrice}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="weight">{t('products.weight')} (kg)</label>
            <div className={styles.inputWithIcon}>
              <Weight size={18} className={styles.inputIcon} />
              <input
                type="number"
                id="weight"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Shop-specific pricing */}
        <div className={styles.shopDataSection}>
          <h3>{t('products.shopPricingInventory')}</h3>
          <p className={styles.sectionDesc}>{t('products.shopPricingInventoryDescSelect')}</p>

          <div className={styles.shopDataGrid}>
            {shops.map((shop, index) => (
              <div
                key={shop.id}
                className={`${styles.shopCard} ${formData.shopData[index]?.enabled ? styles.shopCardEnabled : ''}`}
              >
                <div className={styles.shopHeader}>
                  <label className={styles.shopCheckbox}>
                    <input
                      type="checkbox"
                      checked={formData.shopData[index]?.enabled || false}
                      onChange={(e) => handleShopDataChange(index, 'enabled', e.target.checked)}
                    />
                    <Building2 size={16} className={styles.shopIcon} />
                    <span className={styles.shopName}>{shop.name}</span>
                  </label>
                  <span className={styles.shopType}>{shop.type}</span>
                </div>

                {formData.shopData[index]?.enabled && (
                  <div className={styles.shopFields}>
                    <div className={styles.formGroup}>
                      <label>{t('products.salesPrice')} *</label>
                      <div className={styles.inputWithIcon}>
                        <DollarSign size={16} className={styles.inputIcon} />
                        <input
                          type="number"
                          value={formData.shopData[index]?.salesPrice ?? ''}
                          onChange={(e) => handleShopDataChange(index, 'salesPrice', e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          className={errors.shopData?.[index]?.salesPrice ? 'error' : ''}
                          required
                        />
                      </div>
                      {errors.shopData?.[index]?.salesPrice && (
                        <span className={styles.errorText}>{errors.shopData[index].salesPrice}</span>
                      )}
                    </div>

                    {shop.type === 'dropshipper' && (
                      <div className={styles.formGroup}>
                        <label>{t('products.dropshippingPrice')} *</label>
                        <div className={styles.inputWithIcon}>
                          <DollarSign size={16} className={styles.inputIcon} />
                          <input
                            type="number"
                            value={formData.shopData[index]?.dropshippingPrice || ''}
                            onChange={(e) => handleShopDataChange(index, 'dropshippingPrice', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            className={errors.shopData?.[index]?.dropshippingPrice ? 'error' : ''}
                            required
                          />
                        </div>
                        {errors.shopData?.[index]?.dropshippingPrice && (
                          <span className={styles.errorText}>{errors.shopData[index].dropshippingPrice}</span>
                        )}
                      </div>
                    )}

                    {/* Override Delivery Charge */}
                    <div className={styles.overrideDeliverySection}>
                      <label className={styles.overrideDeliveryCheckbox}>
                        <input
                          type="checkbox"
                          checked={formData.shopData[index]?.overrideDeliveryChargeEnabled || false}
                          onChange={(e) => {
                            handleShopDataChange(index, 'overrideDeliveryChargeEnabled', e.target.checked);
                            if (!e.target.checked) {
                              handleShopDataChange(index, 'overrideDeliveryCharge', null);
                            }
                          }}
                        />
                        <Truck size={14} className={styles.overrideDeliveryIcon} />
                        <span>Override Delivery Charge</span>
                      </label>

                      {formData.shopData[index]?.overrideDeliveryChargeEnabled && (
                        <div className={styles.formGroup}>
                          <div className={styles.inputWithIcon}>
                            <DollarSign size={16} className={styles.inputIcon} />
                            <input
                              type="number"
                              value={formData.shopData[index]?.overrideDeliveryCharge ?? ''}
                              onChange={(e) =>
                                handleShopDataChange(index, 'overrideDeliveryCharge', e.target.value)
                              }
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quantity-Based Offers */}
                    <button
                      type="button"
                      className={`${styles.offersButton} ${formData.shopData[index]?.offers?.length > 0 ? styles.offersButtonActive : ''}`}
                      onClick={() => setOffersModalIndex(index)}
                    >
                      <Tag size={14} />
                      Offers
                      {formData.shopData[index]?.offers?.length > 0 && (
                        <span className={styles.offersBadge}>{formData.shopData[index].offers.length}</span>
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {offersModalIndex !== null && (
          <OffersModal
            isOpen
            onClose={() => setOffersModalIndex(null)}
            onSave={(newOffers) => {
              handleShopDataChange(offersModalIndex, 'offers', newOffers);
              setOffersModalIndex(null);
            }}
            offers={formData.shopData[offersModalIndex]?.offers || []}
            productName={shops[offersModalIndex]?.name || ''}
          />
        )}

        {/* Opening Stock Section */}
        <div className={styles.openingStockSection}>
          <h3>{t('products.openingStock')}</h3>
          <p className={styles.sectionDesc}>{t('products.openingStockDesc')}</p>

          <div className={styles.warehouseStockGrid}>
            {warehouses.map((warehouse, index) => (
              <div key={warehouse.id} className={styles.warehouseCard}>
                <div className={styles.warehouseHeader}>
                  <Warehouse size={16} className={styles.warehouseIcon} />
                  <span className={styles.warehouseName}>{warehouse.name}</span>
                </div>

                <div className={styles.warehouseFields}>
                  <div className={styles.formGroup}>
                    <label>{t('products.openingStock')}</label>
                    <div className={styles.inputWithIcon}>
                      <BarChart3 size={16} className={styles.inputIcon} />
                      <input
                        type="number"
                        value={formData.openingStock[index]?.stock || ''}
                        onChange={(e) => handleOpeningStockChange(index, e.target.value)}
                        placeholder="0"
                        min="0"
                        className={errors.openingStock?.[index]?.stock ? 'error' : ''}
                      />
                    </div>
                    {errors.openingStock?.[index]?.stock && (
                      <span className={styles.errorText}>{errors.openingStock[index].stock}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTab;